import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setTokens, clearTokens, getAccessToken, getRefreshToken } from '../lib/api';
import { Company, Profile, UserMembership, UserRole } from '../types';

export interface AuthUser {
  id: string;
  email: string;
  full_name: string | null;
  email_verified: boolean;
  avatar_url?: string | null;
}

interface MeResponse {
  user: AuthUser;
  profile: Profile;
  company: Company | null;
  memberships: UserMembership[];
}

interface SignUpOptions {
  fullName?: string;
  companyName?: string;
  companyCode?: string;
  requestedRole?: Exclude<UserRole, 'admin'>;
}

interface AuthContextType {
  user: AuthUser | null;
  profile: Profile | null;
  company: Company | null;
  role: UserRole | null;
  memberships: UserMembership[];
  loading: boolean;
  refreshProfile: () => Promise<void>;
  switchCompany: (companyId: string) => Promise<{ error: any }>;
  joinCompany: (inviteCode: string, role: 'developer' | 'tester') => Promise<{ error: any; company_name?: string }>;
  createCompany: (companyName: string, setActive?: boolean) => Promise<{ error: any; company_name?: string; company_id?: string }>;
  signUp: (email: string, password: string, options?: SignUpOptions) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  resendConfirmationEmail: (email: string) => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user,        setUser]        = useState<AuthUser | null>(null);
  const [profile,     setProfile]     = useState<Profile | null>(null);
  const [company,     setCompany]     = useState<Company | null>(null);
  const [memberships, setMemberships] = useState<UserMembership[]>([]);
  const [loading,     setLoading]     = useState(true);

  const applyMe = (me: MeResponse) => {
    setUser(me.user);
    setProfile(me.profile);
    setCompany(me.company ?? null);
    setMemberships(me.memberships ?? []);
  };

  const clearAuth = () => {
    clearTokens();
    setUser(null);
    setProfile(null);
    setCompany(null);
    setMemberships([]);
  };

  // On mount: if tokens exist in localStorage, load the user
  useEffect(() => {
    const token = getAccessToken();
    if (!token) {
      // Try refresh token
      const rt = getRefreshToken();
      if (!rt) { setLoading(false); return; }
      api.post<{ accessToken: string; refreshToken: string }>('/api/auth/refresh', { refreshToken: rt })
        .then(({ accessToken, refreshToken }) => {
          setTokens(accessToken, refreshToken);
          return api.get<MeResponse>('/api/auth/me');
        })
        .then(applyMe)
        .catch(clearAuth)
        .finally(() => setLoading(false));
      return;
    }
    api.get<MeResponse>('/api/auth/me')
      .then(applyMe)
      .catch(clearAuth)
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshProfile = useCallback(async () => {
    try {
      const me = await api.get<MeResponse>('/api/auth/me');
      applyMe(me);
    } catch {
      clearAuth();
    }
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const data = await api.post<{ accessToken: string; refreshToken: string } & MeResponse>(
        '/api/auth/login',
        { email, password }
      );
      setTokens(data.accessToken, data.refreshToken);
      applyMe(data);
      return { error: null };
    } catch (err: any) {
      return { error: { message: err.message, code: err.code } };
    }
  };

  const signUp = async (email: string, password: string, options?: SignUpOptions) => {
    try {
      await api.post('/api/auth/signup', {
        email,
        password,
        fullName:      options?.fullName?.trim(),
        companyName:   options?.companyName?.trim(),
        companyCode:   options?.companyCode?.trim().toUpperCase(),
        requestedRole: options?.requestedRole,
      });
      return { error: null };
    } catch (err: any) {
      return { error: { message: err.message, code: err.code } };
    }
  };

  const signInWithGoogle = async () => {
    // Server-side OAuth redirect — navigates away, no error expected
    window.location.href = '/api/auth/google';
    return { error: null };
  };

  const signOut = async () => {
    const rt = getRefreshToken();
    await api.post('/api/auth/logout', { refreshToken: rt }).catch(() => {});
    clearAuth();
  };

  const resetPassword = async (email: string) => {
    try {
      await api.post('/api/auth/forgot-password', { email });
      return { error: null };
    } catch (err: any) {
      return { error: { message: err.message } };
    }
  };

  const resendConfirmationEmail = async (_email: string) => {
    return { error: null };
  };

  const switchCompany = async (companyId: string) => {
    try {
      const data = await api.post<{ accessToken: string; refreshToken: string } & MeResponse>(
        '/api/company/switch',
        { companyId }
      );
      setTokens(data.accessToken, data.refreshToken);
      applyMe(data);
      return { error: null };
    } catch (err: any) {
      return { error: { message: err.message } };
    }
  };

  const joinCompany = async (inviteCode: string, role: 'developer' | 'tester') => {
    try {
      const data = await api.post<{ ok: boolean; company_name: string }>(
        '/api/company/join',
        { inviteCode: inviteCode.trim().toUpperCase(), role }
      );
      await refreshProfile();
      return { error: null, company_name: data.company_name };
    } catch (err: any) {
      return { error: { message: err.message } };
    }
  };

  const createCompany = async (companyName: string, setActive: boolean = false) => {
    try {
      const data = await api.post<{ ok: boolean; company_name: string; company_id: string }>(
        '/api/company/create',
        { companyName: companyName.trim(), setActive }
      );
      await refreshProfile();
      return { error: null, company_name: data.company_name, company_id: data.company_id };
    } catch (err: any) {
      return { error: { message: err.message } };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        company,
        role: profile?.role ?? null,
        memberships,
        loading,
        refreshProfile,
        switchCompany,
        joinCompany,
        createCompany,
        signUp,
        signIn,
        signInWithGoogle,
        signOut,
        resetPassword,
        resendConfirmationEmail,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}
